import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";

const STATS = [
  { k: "100%", l: "Vendor-neutral" },
  { k: "24/7", l: "Continuous monitoring" },
  { k: "NIST 800-53", l: "Aligned programs" },
  { k: "DMV", l: "Headquartered" },
];

const APPROACH = [
  { n: "01", t: "Secure by design", d: "Every architecture begins with NIST 800-53, Zero Trust, and threat modeling — not bolted on after the fact." },
  { n: "02", t: "Scalable & future-ready", d: "Infrastructure as code, policy as code, and modular services that grow with mission demand." },
  { n: "03", t: "Practical execution", d: "No templates. Every engagement is engineered to your policy boundary, tech stack, and cadence." },
  { n: "04", t: "Transparent collaboration", d: "Traceable decisions, auditable artifacts, and clear governance at every checkpoint." },
];

const VALUES = [
  { t: "Integrity", d: "We tell clients what they need to hear, even when the reseller won't." },
  { t: "Precision", d: "Engineered outcomes measured in controls, SLOs, and dollars recovered." },
  { t: "Security", d: "Defense-in-depth is a default posture, not a line item." },
  { t: "Resilience", d: "Systems that survive failure, disclosure, and adversarial conditions." },
  { t: "Clarity", d: "Plain-language artifacts that auditors, engineers, and executives all trust." },
  { t: "Velocity", d: "Automation-first delivery so mission timelines get shorter, not longer." },
];

const About = () => (
  <>
    <SEO title="About — Strategic advisory, vendor-neutral execution" description="Washington DC federal technology consulting firm delivering Zero Trust cloud, continuous compliance, and mission-critical modernization." path="/about" />

    <section style={{ paddingTop: 120, paddingBottom: 80 }}>
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 28 }}><span className="sep" />About Gravitas</div>
        <h1 className="display-xl" style={{ maxWidth: 1200 }}>
          Strategic advisory.<br />
          <span style={{ color: "var(--green)", fontStyle: "italic", fontWeight: 800 }}>Vendor-neutral</span> execution.
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" style={{ marginTop: 64 }}>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="lede">
              Gravitas is a Washington DC–based consultancy built by engineers who spent careers inside federal SOCs, mission platforms, and hyperscale clouds. We don't sell licenses. We build the evidence package your ATO demands, the pipeline your security team trusts, and the runbook your on-call still wants at 3 a.m.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Stats band */}
    <section style={{ borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="container-grav">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {STATS.map((s, i) => (
            <div key={s.l} style={{ padding: "44px 24px", borderRight: i < STATS.length - 1 ? "1px solid var(--line)" : "none" }}>
              <div className="font-display" style={{ fontSize: 52, fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1 }}>{s.k}</div>
              <div style={{ marginTop: 14, color: "var(--muted)", fontSize: 14 }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Mission / Vision */}
    <section className="section">
      <div className="container-grav">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="tile" style={{ padding: "56px 48px", minHeight: 380 }}>
            <div className="accent-bar" />
            <div className="eyebrow" style={{ marginBottom: 28 }}>Mission</div>
            <h3 className="display-md">Modernize and secure <span className="text-green">mission systems.</span></h3>
            <p className="lede" style={{ marginTop: 24, color: "var(--muted)" }}>
              We enable agency and enterprise programs to adopt Zero Trust, DevSecOps, and continuous compliance — replacing point-in-time audits with automated, evidence-backed posture that survives adversarial conditions.
            </p>
          </div>
          <div className="tile" style={{ padding: "56px 48px", minHeight: 380 }}>
            <div className="accent-bar" />
            <div className="eyebrow" style={{ marginBottom: 28 }}>Vision</div>
            <h3 className="display-md">A more <span className="text-green">resilient</span> public-interest infrastructure.</h3>
            <p className="lede" style={{ marginTop: 24, color: "var(--muted)" }}>
              A federal and enterprise technology layer that is secure by default, vendor-neutral in procurement, and accountable to the missions it serves — from civilian services to defense platforms.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Approach — big editorial rows */}
    <section className="section" style={{ background: "var(--bg-alt)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Approach</div>
        <h2 className="display-lg" style={{ maxWidth: 900 }}>Four principles for delivering in regulated environments.</h2>
        <div style={{ marginTop: 72, borderTop: "1px solid var(--ink)" }}>
          {APPROACH.map((p) => (
            <div key={p.n} className="row-editorial row-3">
              <div className="eyebrow" style={{ color: "var(--ink)" }}>{p.n}</div>
              <h3 className="display-md">{p.t}</h3>
              <p className="body-md" style={{ maxWidth: 520 }}>{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Core Values */}
    <section className="section">
      <div className="container-grav">
        <div className="eyebrow" style={{ marginBottom: 20 }}><span className="sep" />Core values</div>
        <h2 className="display-lg" style={{ maxWidth: 760 }}>The operating principles behind every engagement.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" style={{ marginTop: 64 }}>
          {VALUES.map((v) => (
            <div key={v.t} className="tile" style={{ minHeight: 240 }}>
              <div className="accent-bar" />
              <h3 className="display-sm" style={{ color: "var(--green)", fontWeight: 800 }}>{v.t}</h3>
              <p className="body-md" style={{ marginTop: 12 }}>{v.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section style={{ padding: "120px 0", background: "var(--ink)", color: "var(--dark-ink)" }}>
      <div className="container-grav" style={{ textAlign: "center" }}>
        <h2 className="display-lg" style={{ maxWidth: 900, margin: "0 auto", color: "var(--dark-ink)" }}>
          Brief us on your <span style={{ color: "var(--green)" }}>mission challenge.</span>
        </h2>
        <div style={{ marginTop: 40 }}>
          <Link to="/contact" className="btn btn-light">Request consultation <span className="arrow"><ArrowUpRight size={14} /></span></Link>
        </div>
      </div>
    </section>
  </>
);

export default About;
